/**
 * All-time P&L headline cards (A7Y-2054): Spend, Revenue, Net.
 *
 * Presentational only — takes `PnlSummary['totals']` and renders three cards.
 * Net is colored by sign (green positive, red negative, neutral at zero).
 */
import { cn, formatCurrency } from "@/lib/utils";
import type { PnlSummary } from "@a7y/types";
import { ArrowDownRight, ArrowUpRight, Scale, TrendingUp, Wallet } from "lucide-react";

function Card({
  title,
  value,
  icon,
  iconClass,
  valueClass,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  iconClass: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{title}</p>
          <p
            className={cn(
              "mt-2 text-3xl font-semibold tracking-tight tabular-nums",
              valueClass ?? "text-zinc-950 dark:text-white",
            )}
          >
            {value}
          </p>
        </div>
        <div className={cn("rounded-full p-3 shrink-0", iconClass)}>{icon}</div>
      </div>
    </div>
  );
}

export function SummaryCards({ totals }: { totals: PnlSummary["totals"] }) {
  const net = totals.net;
  const netPositive = net > 0;
  const netNegative = net < 0;

  const netValueClass = netPositive
    ? "text-emerald-600 dark:text-emerald-400"
    : netNegative
      ? "text-rose-600 dark:text-rose-400"
      : "text-zinc-950 dark:text-white";

  const netIconClass = netPositive
    ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
    : netNegative
      ? "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300"
      : "bg-zinc-100 text-zinc-500 dark:bg-zinc-700/50 dark:text-zinc-300";

  const NetIcon = netPositive ? ArrowUpRight : netNegative ? ArrowDownRight : Scale;

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <Card
        title="All-time spend"
        value={formatCurrency(totals.spend)}
        icon={<Wallet className="h-6 w-6" />}
        iconClass="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
      />
      <Card
        title="All-time revenue"
        value={formatCurrency(totals.revenue)}
        icon={<TrendingUp className="h-6 w-6" />}
        iconClass="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300"
      />
      <Card
        title="Net"
        value={`${netNegative ? "−" : ""}${formatCurrency(Math.abs(net))}`}
        valueClass={netValueClass}
        icon={<NetIcon className="h-6 w-6" />}
        iconClass={netIconClass}
      />
    </div>
  );
}
